import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Ban, Clock,
  ScanLine, Search, Keyboard, CameraOff,
} from "lucide-react";

const READER_ID = "st-qr-reader";

const RESULT = {
  ok:                 { bg: "#14532d", ring: "#22c55e", title: "Checked in",         Icon: CheckCircle2 },
  warn_ok:            { bg: "#78350f", ring: "#f59e0b", title: "Checked in — date",  Icon: AlertTriangle },
  already_checked_in: { bg: "#78350f", ring: "#f59e0b", title: "Already checked in", Icon: AlertTriangle },
  wrong_date:         { bg: "#7f1d1d", ring: "#ef4444", title: "Wrong date",         Icon: Clock },
  ticket_cancelled:   { bg: "#7f1d1d", ring: "#ef4444", title: "Ticket cancelled",   Icon: Ban },
  wrong_attraction:   { bg: "#7f1d1d", ring: "#ef4444", title: "Wrong attraction",   Icon: XCircle },
  order_not_confirmed:{ bg: "#7f1d1d", ring: "#ef4444", title: "Not paid",           Icon: XCircle },
  not_found:          { bg: "#7f1d1d", ring: "#ef4444", title: "Ticket not found",   Icon: XCircle },
};

async function doCheckin(id) {
  const r = await api.post("/checkin", { attendee_id: id, device: "mobile-scan", force: true });
  return r.data;
}

function resultFromResponse(res) {
  if (res.ok) {
    return { kind: res.warning ? "warn_ok" : "ok", sub: res.attendee?.name || "" };
  }
  if (res.reason === "already_checked_in") {
    return { kind: "already_checked_in", sub: `${res.attendee?.name || ""} @ ${res.checked_in_at?.slice(11, 16) || ""}` };
  }
  if (res.reason === "wrong_date") {
    return { kind: "wrong_date", sub: `Ticket is for ${res.visit_date}, not today` };
  }
  return { kind: res.reason || "not_found", sub: res.attendee?.name || "Try again" };
}

export default function ScanCheckinPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState("starting"); // starting | scanning | error
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState(null); // { kind, sub }
  const [count, setCount] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const [manualVal, setManualVal] = useState("");

  const scannerRef = useRef(null);
  const runningRef = useRef(false);
  const busyRef = useRef(false);
  const recentRef = useRef({});
  const resultTimer = useRef(null);

  const process = async (raw) => {
    const id = (raw || "").trim();
    if (!id || busyRef.current) return;
    const now = Date.now();
    if (recentRef.current[id] && now - recentRef.current[id] < 4000) return;
    // purge stale debounce entries so the map can't grow unbounded on a long shift
    for (const k of Object.keys(recentRef.current)) {
      if (now - recentRef.current[k] > 15000) delete recentRef.current[k];
    }
    recentRef.current[id] = now;
    busyRef.current = true;
    let res;
    try {
      res = await doCheckin(id);
    } catch {
      res = { ok: false, reason: "not_found" };
    }
    const view = resultFromResponse(res);
    if (res.ok) setCount((c) => c + 1);
    try { navigator.vibrate?.(res.ok ? 60 : [40, 60, 40]); } catch { /* noop */ }
    setResult(view);
    clearTimeout(resultTimer.current);
    resultTimer.current = setTimeout(() => setResult(null), 2200);
    setTimeout(() => { busyRef.current = false; }, 1200);
  };

  const submitManual = (e) => {
    e.preventDefault();
    const v = manualVal.trim();
    if (!v) return;
    setManualVal("");
    process(v);
  };

  useEffect(() => {
    let active = true;
    const initPromise = (async () => {
      const mod = await import("html5-qrcode");
      if (!active) return;
      const scanner = new mod.Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;
      const qrbox = (vw, vh) => {
        const edge = Math.floor(Math.min(vw, vh) * 0.7);
        return { width: edge, height: edge };
      };
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox }, (text) => process(text), () => {});
      if (!active) { try { await scanner.stop(); await scanner.clear(); } catch { /* noop */ } return; }
      runningRef.current = true;
      setStatus("scanning");
    })().catch((e) => {
      if (!active) return;
      setStatus("error");
      const name = e?.name || "";
      if (name === "NotAllowedError") setErrMsg("Camera permission was denied. Allow camera access, then reload.");
      else if (name === "NotFoundError") setErrMsg("No camera found on this device.");
      else setErrMsg("Could not start the camera. You can still enter ticket IDs manually below.");
    });

    return () => {
      active = false;
      clearTimeout(resultTimer.current);
      (async () => {
        try { await initPromise; } catch { /* noop */ }
        const s = scannerRef.current;
        if (s) {
          try {
            if (s.getState && s.getState() === 2) await s.stop();
            await s.clear();
          } catch { /* noop */ }
        }
        runningRef.current = false;
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cfg = result ? RESULT[result.kind] : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" data-testid="scan-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a] bg-[#0c0c0c]">
        <button onClick={() => nav("/dashboard/checkin")} className="st-btn st-btn-ghost" data-testid="scan-back">
          <ArrowLeft size={16} /> Check-in
        </button>
        <div className="st-brand text-lg">SCARE<span className="text-[#dc2626]">TICKETS</span></div>
        <div className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-[#14532d]/40 border border-[#22c55e]/30" data-testid="scan-count">
          <CheckCircle2 size={15} className="text-[#22c55e]" /> {count}
        </div>
      </div>

      {/* Camera viewport */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden bg-black">
        <div id={READER_ID} className="w-full max-w-[520px] mx-auto" />

        {status === "scanning" && !result && (
          <div className="pointer-events-none absolute inset-x-0 bottom-6 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-white/80 bg-black/50 px-3 py-1.5 rounded-full">
              <ScanLine size={16} className="text-[#dc2626]" /> Point the camera at a ticket QR
            </div>
          </div>
        )}

        {status === "starting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
            <ScanLine size={48} className="text-[#dc2626] animate-pulse" />
            <div>Starting camera…</div>
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-8" data-testid="scan-error">
            <CameraOff size={48} className="text-[#ef4444]" />
            <div className="text-white/80 max-w-xs">{errMsg}</div>
          </div>
        )}

        {/* Result overlay */}
        {result && cfg && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 transition-colors"
            style={{ background: cfg.bg }}
            data-testid={`scan-result-${result.kind}`}
          >
            <cfg.Icon size={110} className="opacity-90" style={{ color: cfg.ring }} />
            <div className="text-4xl font-extrabold mt-5">{cfg.title}</div>
            {result.sub && <div className="text-xl text-white/85 mt-2">{result.sub}</div>}
          </div>
        )}
      </div>

      {/* Footer controls */}
      <div className="border-t border-[#1a1a1a] bg-[#0c0c0c] p-4 space-y-3">
        <div className="flex gap-2">
          <button
            className="st-btn flex-1 justify-center"
            onClick={() => setShowManual((s) => !s)}
            data-testid="scan-toggle-manual"
          >
            <Keyboard size={16} /> {showManual ? "Hide manual entry" : "Enter ID manually"}
          </button>
          <button
            className="st-btn st-btn-ghost flex-1 justify-center"
            onClick={() => nav("/dashboard/checkin")}
            data-testid="scan-search-link"
          >
            <Search size={16} /> Search by name
          </button>
        </div>
        {showManual && (
          <form onSubmit={submitManual} className="flex gap-2" data-testid="scan-manual-form">
            <input
              className="st-input flex-1"
              placeholder="Paste or type ticket ID"
              value={manualVal}
              onChange={(e) => setManualVal(e.target.value)}
              data-testid="scan-manual-input"
            />
            <button type="submit" className="st-btn" data-testid="scan-manual-submit">Check in</button>
          </form>
        )}
      </div>
    </div>
  );
}
